/* ================================================================
   Search overlay controller.
   Mirrors the mobile nav overlay pattern in sections/header.liquid:
   vanilla IIFE, plain DOM, aria-hidden toggle, moved to body,
   intercepts any element with [data-search-trigger] or the legacy
   .header__icon--search summary button.
   ================================================================ */
(function () {
  'use strict';

  var overlay = document.getElementById('SearchOverlay');
  if (!overlay) return;

  /* Move to body so it escapes any containing blocks (transforms, filters, overflow). */
  if (overlay.parentNode !== document.body) {
    document.body.appendChild(overlay);
  }

  var panel = overlay.querySelector('.search-overlay__panel');
  var input = overlay.querySelector('.search-overlay__input');
  var form = overlay.querySelector('.search-overlay__form');
  var clearBtn = overlay.querySelector('[data-search-overlay-clear]');
  var empty = overlay.querySelector('[data-search-overlay-empty]');
  var recentShelf = overlay.querySelector('[data-recent-shelf]');
  var recentList = overlay.querySelector('[data-recent-list]');
  var recentClear = overlay.querySelector('[data-recent-clear]');
  var predictive = overlay.querySelector('predictive-search');

  var RECENT_KEY = 'relevance:search:recent';
  var RECENT_MAX = 5;
  var returnFocusEl = null;
  var scrollY = 0;

  /* ── Scroll lock (iOS-safe using position:fixed with stored scrollY) ── */
  function lockScroll() {
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* ── Open / close ────────────────────────────────────────────── */
  function openOverlay(trigger) {
    if (overlay.getAttribute('aria-hidden') === 'false') return;
    returnFocusEl = trigger || document.activeElement;
    lockScroll();
    overlay.setAttribute('aria-hidden', 'false');
    renderRecent();
    /* Focus input after the transition starts, so mobile Safari doesn't
       scroll the underlying page up to meet the input. */
    setTimeout(function () {
      input && input.focus();
    }, 60);
  }

  function closeOverlay() {
    if (overlay.getAttribute('aria-hidden') === 'true') return;
    overlay.setAttribute('aria-hidden', 'true');
    unlockScroll();
    /* Close predictive-search so [open] attribute is cleared */
    if (predictive && typeof predictive.close === 'function') {
      try { predictive.close(false); } catch (_) {}
    }
    if (returnFocusEl && typeof returnFocusEl.focus === 'function') {
      try { returnFocusEl.focus(); } catch (_) {}
    }
    returnFocusEl = null;
  }

  /* ── Trigger wiring ──────────────────────────────────────────── */
  function wireTriggers() {
    /* Any explicit [data-search-trigger] opens the overlay */
    document.querySelectorAll('[data-search-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openOverlay(btn);
      });
    });

    /* Legacy fallback: intercept any remaining .header__icon--search summary buttons
       in case the old details-modal markup is still rendered. Prevents the
       <details> from toggling, opens our overlay instead. */
    document.querySelectorAll('.header__icon--search').forEach(function (el) {
      if (el.hasAttribute('data-search-trigger')) return;
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var det = el.closest('details');
        if (det) {
          det.removeAttribute('open');
        }
        openOverlay(el);
      }, true);
    });
  }

  /* ── Close handlers ──────────────────────────────────────────── */
  overlay.querySelectorAll('[data-search-overlay-close]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      closeOverlay();
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') === 'false') {
      closeOverlay();
    }
  });

  /* ── Clear button (resets input + shows empty state) ─────────── */
  if (clearBtn && input) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.hidden = true;
      overlay.removeAttribute('data-state');
      if (predictive && typeof predictive.close === 'function') {
        try { predictive.close(false); } catch (_) {}
      }
      input.focus();
    });
  }

  /* Toggle clear button + empty/results state based on input content */
  if (input) {
    input.addEventListener('input', function () {
      var hasValue = input.value.trim().length > 0;
      if (clearBtn) clearBtn.hidden = !hasValue;
      overlay.setAttribute('data-state', hasValue ? 'results' : 'empty');
    });
  }

  /* ── Form submit: save to recent, then navigate naturally ────── */
  if (form) {
    form.addEventListener('submit', function () {
      var q = (input && input.value || '').trim();
      if (q) pushRecent(q);
      /* Let the browser navigate to /search?q=... */
    });
  }

  /* ── Popular search chip clicks: also save to recent ─────────── */
  overlay.querySelectorAll('[data-popular-term]').forEach(function (a) {
    a.addEventListener('click', function () {
      pushRecent(a.getAttribute('data-popular-term') || a.textContent.trim());
    });
  });

  /* ── Recent searches (localStorage) ──────────────────────────── */
  function readRecent() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (_) {
      return [];
    }
  }

  function writeRecent(arr) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(arr));
    } catch (_) {}
  }

  function pushRecent(term) {
    term = (term || '').trim();
    if (!term) return;
    var list = readRecent().filter(function (item) {
      return (item.q || '').toLowerCase() !== term.toLowerCase();
    });
    list.unshift({ q: term, ts: Date.now() });
    if (list.length > RECENT_MAX) list = list.slice(0, RECENT_MAX);
    writeRecent(list);
  }

  function removeRecent(term) {
    var list = readRecent().filter(function (item) {
      return (item.q || '').toLowerCase() !== (term || '').toLowerCase();
    });
    writeRecent(list);
    renderRecent();
  }

  function clearRecent() {
    writeRecent([]);
    renderRecent();
  }

  function renderRecent() {
    if (!recentShelf || !recentList) return;
    var list = readRecent();
    if (!list.length) {
      recentShelf.hidden = true;
      recentList.innerHTML = '';
      return;
    }
    recentShelf.hidden = false;
    var searchUrl = (window.routes && window.routes.search_url) || '/search';
    recentList.innerHTML = list.map(function (item) {
      var q = (item.q || '').replace(/[<>"']/g, function (c) {
        return ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
      });
      var href = searchUrl + '?q=' + encodeURIComponent(item.q) + '&options%5Bprefix%5D=last';
      return (
        '<li>' +
          '<span class="search-overlay__chip search-overlay__chip--recent">' +
            '<a href="' + href + '" data-recent-term="' + q + '" style="color:inherit;text-decoration:none;">' + q + '</a>' +
            '<button type="button" class="search-overlay__chip-remove" aria-label="Remove from recent" data-recent-remove="' + q + '">' +
              '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
            '</button>' +
          '</span>' +
        '</li>'
      );
    }).join('');
  }

  if (recentClear) {
    recentClear.addEventListener('click', clearRecent);
  }

  /* Event delegation for recent-list interactions (rendered dynamically) */
  if (recentList) {
    recentList.addEventListener('click', function (e) {
      var removeBtn = e.target.closest && e.target.closest('[data-recent-remove]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        removeRecent(removeBtn.getAttribute('data-recent-remove'));
        return;
      }
      /* Recent chip click: also fill the input and trigger predictive search */
      var termLink = e.target.closest && e.target.closest('[data-recent-term]');
      if (termLink && input) {
        var term = termLink.getAttribute('data-recent-term');
        pushRecent(term);
        /* Let the anchor navigate naturally — simpler than hijacking to fill the input. */
      }
    });
  }

  /* ── Initialise ──────────────────────────────────────────────── */
  wireTriggers();
  /* Re-wire on DOMContentLoaded too, in case triggers render later */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireTriggers);
  }
})();
